import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useAssessmentMutations } from "../hooks/useAssessmentMutations";
import {
  DataActAnswer,
  DataActSection,
  DataActQuestionsJSON,
  DataActAssessment,
  DataActResult,
  Product,
  canManageAssessment,
} from "../types";
import {
  getVisibleSections,
  getVisibleQuestions,
  pruneUnreachableAnswers,
  sectionDisplayTitle,
} from "../utils/dataActConditions";
import AssessmentPageShell from "../components/assessment/AssessmentPageShell";
import DataActAssessmentQuestion from "../components/DataActAssessmentQuestion";
import DataActAssessmentFeedback from "../components/DataActAssessmentFeedback";
import DataActPrerequisiteGate from "../components/DataActPrerequisiteGate";
import DataActLinkedProductFlow from "../components/DataActLinkedProductFlow";
import {
  linkedFlowQuestionIds,
  linkedFlowStateQuestionId,
} from "../utils/dataActLinkedFlow";
import {
  CheckCircle,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  PlayCircle,
  ArrowLeft,
  ArrowDown,
  Save,
} from "lucide-react";

type AnswerMap = Record<string, { answer: string; details: string }>;

function answersToList(map: AnswerMap): DataActAnswer[] {
  return Object.entries(map)
    .filter(([, v]) => v.answer !== "")
    .map(([questionId, v]) => ({
      questionId,
      answer: v.answer,
      details: v.details,
    }));
}

function listToAnswerMap(list: DataActAnswer[]): AnswerMap {
  const map: AnswerMap = {};
  for (const a of list) {
    map[a.questionId] = { answer: a.answer, details: a.details };
  }
  // Migrate legacy drafts where the linked-product picker state was stored as
  // JSON in Q2.5 / Q2.11 details. Move it to the synthetic state key so the
  // user-facing "Additional Details" textarea is not polluted with machine
  // state.
  for (const triggerId of ["Q2.5", "Q2.11"] as const) {
    const entry = map[triggerId];
    if (!entry?.details) continue;
    try {
      const parsed = JSON.parse(entry.details);
      if (
        parsed &&
        typeof parsed === "object" &&
        (parsed.mode === "linked" || parsed.mode === "manual")
      ) {
        const stateId = linkedFlowStateQuestionId(
          triggerId === "Q2.5" ? "related-service" : "connected-product",
        );
        const peerIds = Array.isArray(parsed.peerProductIds)
          ? parsed.peerProductIds.filter(
              (v: unknown): v is string => typeof v === "string",
            )
          : [];
        map[stateId] = {
          answer: parsed.mode,
          details: peerIds.length > 0 ? JSON.stringify(peerIds) : "",
        };
        map[triggerId] = { ...entry, details: "" };
      }
    } catch {
      // Not JSON — leave as-is (free-form user note from before the picker).
    }
  }
  return map;
}

function resolvePrefill(prefillKey: string, product: Product): string {
  switch (prefillKey) {
    case "product.name":
      return product.name;
    case "product.businessArea":
      return product.businessArea;
    case "product.division":
      return product.division;
    case "product.owner":
      return product.owner?.name ?? "";
    case "product.productType":
      return resolveProductType(product) ?? "";
    default:
      return "";
  }
}

/**
 * Derives Q1.1 from the product's `product-type` tag.
 * Returns "product" for hardware, "service" for software, null otherwise.
 */
function resolveProductType(product: Product): "product" | "service" | null {
  const tag = product.productTags?.find((t) => t.key === "product-type");
  if (tag?.value === "hardware") return "product";
  if (tag?.value === "software") return "service";
  return null;
}

export default function DataActAssessmentPage() {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get("view");
  const shouldResume = searchParams.get("resume") === "1";
  const { user } = useAuth();

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [showFeedback, setShowFeedback] = useState(false);
  const [submittedResult, setSubmittedResult] = useState<DataActResult | null>(
    null,
  );
  const [assessmentStarted, setAssessmentStarted] = useState(false);

  // Refs for each rendered question (for scroll-to-next-required) and a set
  // tracking which question IDs have been seen so we only animate-in newly
  // revealed follow-ups, not the initial render or already-known questions.
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const seenQuestionIds = useRef<Set<string>>(new Set());

  const isViewMode = !!viewId;

  // ── Queries ──

  const { data: productRes, isLoading: productLoading } = useQuery(
    ["product", productId],
    () => apiService.getProduct(productId!),
    { enabled: !!productId },
  );
  const product: Product | null = productRes?.data?.data ?? null;

  const { data: questionsRes, isLoading: questionsLoading } = useQuery(
    "dataActQuestions",
    () => apiService.getDataActQuestions(),
  );
  const questionsData: DataActQuestionsJSON | null =
    questionsRes?.data?.data ?? null;
  const allSections: DataActSection[] = questionsData?.sections ?? [];

  const { data: latestRes, isLoading: latestLoading } = useQuery(
    ["dataActLatest", productId],
    () => apiService.getLatestDataActAssessment(productId!),
    { enabled: !!productId && !viewId, retry: false },
  );

  const { data: viewRes, isLoading: viewLoading } = useQuery(
    ["dataActAssessment", viewId],
    () => apiService.getDataActAssessment(viewId!),
    { enabled: !!viewId },
  );

  const latestAssessment: DataActAssessment | null =
    (viewId ? viewRes?.data?.data : latestRes?.data?.data) ?? null;

  // Fetch divisions from backend, filtered by business area (Q1.4 answer)
  const businessArea = answers["Q1.4"]?.answer || product?.businessArea || "";
  const { data: divisionsResponse } = useQuery(
    ["divisions", businessArea],
    () => apiService.getDivisions(businessArea),
    {
      enabled: !!businessArea,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  const { data: previousRes } = useQuery(
    ["dataActPrevious", productId, currentVersion],
    () => apiService.getPreviousDataActAssessment(productId!, currentVersion),
    { enabled: !!productId && currentVersion > 1 && !isViewMode, retry: false },
  );
  const previousAnswers: DataActAnswer[] = (() => {
    const prev = previousRes?.data?.data;
    if (!prev?.answers) return [];
    try {
      const parsed =
        typeof prev.answers === "string"
          ? JSON.parse(prev.answers)
          : prev.answers;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();
  const previousVersion: number | null =
    previousRes?.data?.data?.version ?? null;

  // ── Load existing assessment answers ──

  useEffect(() => {
    if (!latestAssessment || !allSections.length) return;

    setExistingId(latestAssessment.id);
    setCurrentVersion(latestAssessment.version);

    if (latestAssessment.status === "submitted" && !isViewMode) {
      setCurrentVersion(latestAssessment.version + 1);
      setExistingId(null);
      try {
        const parsed =
          typeof latestAssessment.answers === "string"
            ? JSON.parse(latestAssessment.answers)
            : latestAssessment.answers;
        if (Array.isArray(parsed)) {
          setAnswers(listToAnswerMap(parsed));
        }
      } catch {
        // Start fresh
      }
    } else {
      try {
        const parsed =
          typeof latestAssessment.answers === "string"
            ? JSON.parse(latestAssessment.answers)
            : latestAssessment.answers;
        if (Array.isArray(parsed)) {
          setAnswers(listToAnswerMap(parsed));
        }
      } catch {
        // Start fresh
      }
      if (latestAssessment.currentPage >= 0) {
        setActiveSectionIdx(latestAssessment.currentPage);
      }
      if (latestAssessment.status === "submitted") {
        setShowFeedback(true);
        try {
          const result =
            typeof latestAssessment.result === "string"
              ? JSON.parse(latestAssessment.result)
              : latestAssessment.result;
          setSubmittedResult(result);
        } catch {
          // No result
        }
      }
    }
  }, [latestAssessment, allSections.length, isViewMode]);

  // ── Auto-fill prefill fields for new assessments ──

  useEffect(() => {
    if (!product || !allSections.length || existingId || isViewMode) return;

    setAnswers((prev) => {
      const updated = { ...prev };
      for (const section of allSections) {
        for (const q of section.questions) {
          if (q.prefill && !updated[q.id]?.answer) {
            const value = resolvePrefill(q.prefill, product);
            if (value) {
              updated[q.id] = { answer: value, details: "" };
            }
          }
        }
      }
      return updated;
    });
  }, [product, allSections, existingId, isViewMode]);

  // Q1.1 is always derived from the product's `product-type` tag — surveys
  // must never become a source of truth for product attributes. This runs in
  // addition to the generic prefill so it also overrides stale draft values.
  useEffect(() => {
    if (!product || isViewMode) return;
    const derived = resolveProductType(product);
    if (!derived) return;
    setAnswers((prev) => {
      if (prev["Q1.1"]?.answer === derived) return prev;
      return {
        ...prev,
        "Q1.1": { answer: derived, details: prev["Q1.1"]?.details ?? "" },
      };
    });
  }, [product, isViewMode]);

  // Reset Q1.5 (division) when Q1.4 (business area) changes and the current
  // division answer is no longer in the list of available divisions.
  useEffect(() => {
    if (!divisionsResponse) return;
    const currentDivision = answers["Q1.5"]?.answer;
    if (currentDivision && !divisionsResponse.includes(currentDivision)) {
      setAnswers((prev) => ({
        ...prev,
        "Q1.5": { answer: "", details: prev["Q1.5"]?.details ?? "" },
      }));
    }
  }, [divisionsResponse]);

  // ── Derived state ──

  const answersList = useMemo(() => answersToList(answers), [answers]);

  // Drives per-path question and section numbering ("product" | "service").
  const pathKey = answers["Q1.1"]?.answer ?? null;

  const visibleSections = useMemo(
    () => getVisibleSections(allSections, answersList),
    [allSections, answersList],
  );

  const currentSection = visibleSections[activeSectionIdx] ?? null;

  const visibleQuestions = useMemo(
    () =>
      currentSection ? getVisibleQuestions(currentSection, answersList) : [],
    [currentSection, answersList],
  );

  // Override Q1.5 options with divisions fetched from the backend
  const divisionOptions = useMemo(() => {
    if (!divisionsResponse) return undefined;
    return divisionsResponse.map((d: string) => ({ value: d, label: d }));
  }, [divisionsResponse]);

  const canEdit =
    canManageAssessment(user, product?.ownerId) && !isViewMode && !showFeedback;

  const isLoading =
    productLoading || questionsLoading || latestLoading || viewLoading;
  const error = !productId
    ? "Product ID is missing"
    : !isLoading && !product
      ? "Product not found"
      : null;

  const hasDraft =
    latestAssessment?.status === "draft" && !isViewMode && !showFeedback;

  // When the user is redirected back from editing the product mid-assessment
  // (e.g. after adjusting Links from inside the linked-product picker), skip
  // the "Continue Assessment" landing and drop them straight onto the section
  // they left. Only do this when there is an in-progress draft to resume — the
  // product-type gate redirects here before any draft exists, and in that case
  // we want the user to see the intro landing rather than jump into questions.
  useEffect(() => {
    if (shouldResume && (hasDraft || existingId)) {
      setAssessmentStarted(true);
    }
  }, [shouldResume, hasDraft, existingId]);

  // Gate: Q1.1 must be derivable from the product's `product-type` tag.
  const derivedProductType = product ? resolveProductType(product) : null;
  const currentProductTypeTag = product?.productTags?.find(
    (t) => t.key === "product-type",
  )?.value;
  const showPrerequisiteGate =
    !!product && !isViewMode && !showFeedback && derivedProductType === null;

  const q1_1Guidance = useMemo(() => {
    for (const s of allSections) {
      for (const q of s.questions) {
        if (q.id === "Q1.1") return q.guidance;
      }
    }
    return undefined;
  }, [allSections]);

  // Progress calculations (exclude infoOnly questions)
  const totalAnswered = useMemo(() => {
    let count = 0;
    for (const section of visibleSections) {
      const qs = getVisibleQuestions(section, answersList);
      count += qs.filter((q) => !q.infoOnly && !!answers[q.id]?.answer).length;
    }
    return count;
  }, [visibleSections, answersList, answers]);

  const totalQuestions = useMemo(() => {
    let count = 0;
    for (const section of visibleSections) {
      count += getVisibleQuestions(section, answersList).filter(
        (q) => !q.infoOnly,
      ).length;
    }
    return count;
  }, [visibleSections, answersList]);

  // Flat ordered list of required, non-infoOnly questions across all visible
  // sections. Used to find the next unanswered required question and the
  // overall remaining count for the floating pill.
  const orderedRequiredQueue = useMemo(() => {
    const out: { sectionIdx: number; questionId: string }[] = [];
    visibleSections.forEach((section, sectionIdx) => {
      for (const q of getVisibleQuestions(section, answersList)) {
        if (q.required && !q.infoOnly) {
          out.push({ sectionIdx, questionId: q.id });
        }
      }
    });
    return out;
  }, [visibleSections, answersList]);

  const nextRequired = useMemo(() => {
    // Prefer a question on the current section so the user keeps momentum.
    const inCurrent = orderedRequiredQueue.find(
      (e) =>
        e.sectionIdx === activeSectionIdx && !answers[e.questionId]?.answer,
    );
    if (inCurrent) return inCurrent;
    return (
      orderedRequiredQueue.find((e) => !answers[e.questionId]?.answer) ?? null
    );
  }, [orderedRequiredQueue, activeSectionIdx, answers]);

  const remainingRequiredCount = useMemo(
    () =>
      orderedRequiredQueue.filter((e) => !answers[e.questionId]?.answer).length,
    [orderedRequiredQueue, answers],
  );

  const scrollToQuestion = useCallback((qid: string) => {
    const el = questionRefs.current[qid];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusable = el.querySelector<HTMLElement>(
      "input,button,textarea,select,[tabindex]",
    );
    focusable?.focus({ preventScroll: true });
  }, []);

  const goToNextRequired = useCallback(() => {
    if (!nextRequired) return;
    if (nextRequired.sectionIdx !== activeSectionIdx) {
      setActiveSectionIdx(nextRequired.sectionIdx);
      // Defer scroll until after the new section renders.
      setTimeout(() => scrollToQuestion(nextRequired.questionId), 80);
      return;
    }
    scrollToQuestion(nextRequired.questionId);
  }, [nextRequired, activeSectionIdx, scrollToQuestion]);

  // ── Mutations ──

  const { saveMutation, submitMutation, isSaving, isSubmitting } =
    useAssessmentMutations(
      {
        saveFn: (payload: any) =>
          existingId
            ? apiService.updateDataActAssessment(existingId, payload)
            : apiService.createDataActAssessment(productId!, payload),
        submitFn: (assessmentId: string) =>
          apiService.submitDataActAssessment(assessmentId),
        invalidateKeys: [
          ["dataActLatest", productId!],
          ["dataActAssessments", productId!],
          ["product", productId!],
          ["assessmentStatus", productId!],
        ],
        // On draft save the page's local state is already authoritative —
        // refetching `dataActLatest` would re-run the load effect, replace the
        // answers map, and reset the scroll position. Other consumers (e.g.
        // the panel on the product detail page) refetch on mount.
        saveInvalidateKeys: [
          ["dataActAssessments", productId!],
          ["assessmentStatus", productId!],
        ],
        entityLabel: "Assessment",
      },
      {
        onSaveSuccess: (response: any) => {
          const newId = response?.data?.data?.id;
          if (newId && !existingId) {
            setExistingId(newId);
          }
        },
        onSubmitSuccess: (response: any) => {
          const data = response?.data?.data;
          if (data?.result) {
            try {
              const result =
                typeof data.result === "string"
                  ? JSON.parse(data.result)
                  : data.result;
              setSubmittedResult(result);
            } catch {
              // No result
            }
          }
          setShowFeedback(true);
        },
      },
    );

  // ── Handlers ──

  const handleChange = (
    questionId: string,
    field: "answer" | "details",
    value: string,
  ) => {
    setAnswers((prev) => {
      const merged: AnswerMap = {
        ...prev,
        [questionId]: {
          answer: prev[questionId]?.answer ?? "",
          details: prev[questionId]?.details ?? "",
          [field]: value,
        },
      };
      // A details edit never changes which questions are reachable, so skip
      // the prune. Only an answer change can flip a branch condition.
      if (field !== "answer") return merged;
      // Collapse the whole downstream chain when an earlier branch changes
      // (e.g. 1.1 Yes → No) instead of stranding stale answers that keep
      // later questions visible and get persisted to the backend.
      const reachable = pruneUnreachableAnswers(
        allSections,
        Object.entries(merged).map(([id, v]) => ({
          questionId: id,
          answer: v.answer,
          details: v.details,
        })),
      );
      const next: AnswerMap = {};
      for (const a of reachable) {
        next[a.questionId] = { answer: a.answer, details: a.details };
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!canEdit) return;
    saveMutation.mutate({
      answers: answersToList(answers),
      version: String(currentVersion),
      currentPage: activeSectionIdx,
    });
  };

  const handleSubmit = () => {
    if (!canEdit) return;

    const missing: string[] = [];
    for (const section of visibleSections) {
      const qs = getVisibleQuestions(section, answersList);
      for (const q of qs) {
        if (q.required && !q.infoOnly && !answers[q.id]?.answer) {
          missing.push(q.id);
        }
      }
    }
    if (missing.length > 0) {
      toast.error(
        `Please answer all required questions. Missing: ${missing.slice(0, 5).join(", ")}${missing.length > 5 ? ` and ${missing.length - 5} more` : ""}`,
      );
      return;
    }

    const payload = {
      answers: answersToList(answers),
      version: String(currentVersion),
      currentPage: activeSectionIdx,
    };

    const doSubmit = (assessmentId: string) => {
      submitMutation.mutate(assessmentId);
    };

    if (existingId) {
      apiService
        .updateDataActAssessment(existingId, payload)
        .then(() => doSubmit(existingId))
        .catch(() => toast.error("Failed to save before submitting"));
    } else {
      apiService
        .createDataActAssessment(productId!, payload)
        .then((res) => {
          const newId = res?.data?.data?.id;
          if (newId) {
            setExistingId(newId);
            doSubmit(newId);
          }
        })
        .catch(() => toast.error("Failed to create assessment"));
    }
  };

  // ── Render ──

  if (showFeedback && submittedResult && product) {
    return (
      <AssessmentPageShell
        title="Data Act Assessment — Results"
        subtitle={product.name}
        backTo={`/products/${productId}`}
        showActions={false}
      >
        <div className="max-w-3xl mx-auto py-8 px-6">
          <DataActAssessmentFeedback
            productName={product.name}
            version={currentVersion}
            result={submittedResult}
          />
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => navigate(`/products/${productId}`)}
              className="btn btn-primary"
            >
              Back to Product
            </button>
          </div>
        </div>
      </AssessmentPageShell>
    );
  }

  // "Continue Assessment" landing for existing drafts
  const showContinueLanding = hasDraft && !assessmentStarted;
  // "Start Assessment" landing for new assessments
  const showStartLanding =
    !hasDraft && !existingId && !isViewMode && !assessmentStarted;

  return (
    <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center space-x-3 flex-shrink-0">
        <button
          onClick={() => navigate(`/products/${productId}`)}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
            Data Act Assessment
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {product?.name}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 mt-2">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            Version:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              v{currentVersion}
            </span>
          </span>
          <span>
            {totalAnswered}/{totalQuestions} answered
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1.5">
          <div
            className="bg-primary-600 h-1.5 rounded-full transition-all"
            style={{
              width: `${totalQuestions ? (totalAnswered / totalQuestions) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      {/* Main Content */}
      {isLoading ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Loading assessment...
            </p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate(`/products/${productId}`)}
              className="btn btn-secondary"
            >
              Back to Product
            </button>
          </div>
        </div>
      ) : showPrerequisiteGate ? (
        <DataActPrerequisiteGate
          productId={productId!}
          productName={product?.name}
          currentProductType={currentProductTypeTag}
          guidance={q1_1Guidance}
        />
      ) : showContinueLanding ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-lg px-6">
            <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
              <ClipboardList className="h-10 w-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Continue Data Act Assessment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
              You have a draft assessment in progress. Your previously saved
              answers will be loaded when you continue.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/products/${productId}`)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-primary text-sm w-full sm:w-auto"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                Continue Assessment
              </button>
            </div>
          </div>
        </div>
      ) : showStartLanding ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex flex-col min-h-0">
          {/* Content area */}
          <div className="flex-1 min-h-0 px-6 py-3 flex flex-col gap-3 overflow-y-auto">
            {/* Hero header */}
            <div className="relative rounded-lg bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-purple-900/10 border border-blue-100 dark:border-blue-800/30 px-6 py-5 overflow-hidden">
              <div className="relative flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-blue-200 dark:border-blue-700/50 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="h-7 w-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                    Data Act Assessment
                  </h3>
                  <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <span>EU Data Act (Regulation 2023/2854)</span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span>📚 {allSections.length} sections</span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span>
                      📝{" "}
                      {allSections.reduce(
                        (sum, s) =>
                          sum + s.questions.filter((q) => !q.infoOnly).length,
                        0,
                      )}{" "}
                      questions
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      ✅ Draft &amp; submit supported
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* How to Use This Tool — 3 info cards */}
            {(() => {
              const cards = [
                {
                  key: "about",
                  emoji: "📋",
                  title: "About the Data Act",
                  border: "border-blue-100 dark:border-blue-800/40",
                  bg: "from-blue-50/80 to-white dark:from-blue-900/15 dark:to-gray-900",
                  iconBg: "bg-blue-100 dark:bg-blue-900/40",
                  body: (
                    <>
                      <p className="mb-2">
                        The Data Act (Regulation (EU) 2023/2854) is part of
                        EU&rsquo;s broader digital strategy. Its aim is to
                        promote a fair, competitive, and innovative data economy
                        by improving access to and use of data across sectors.
                        The Data Act applies across all industries and affects
                        any business that holds, shares, or is required to make
                        data available to third parties. This includes both
                        business-to-business contracts and all types of data,
                        not just personal data. For Sandvik, the Data Act may
                        apply where we:
                      </p>
                      <ul className="list-disc pl-4 space-y-1 mb-2">
                        <li>
                          <span className="font-medium">
                            Manufacture Connected Products
                          </span>{" "}
                          – a physical product that collect or generate data
                          through embedded sensors, software, or connectivity
                          features. General examples by the law are so-called
                          &lsquo;Internet of Things&rsquo;. Translated for
                          Sandvik, examples can be: drilling equipment, mining
                          machinery, or manufacturing tools with data logging or
                          remote monitoring functions.
                        </li>
                        <li>
                          <span className="font-medium">
                            Provide Related Services
                          </span>{" "}
                          – a digital service, incl. software, linked to a
                          Connected Product that rely on or enable the use of
                          data. Examples: predictive maintenance, performance
                          dashboards, or digital twin services.
                        </li>
                      </ul>
                    </>
                  ),
                },
                {
                  key: "purpose",
                  emoji: "🎯",
                  title: "Purpose of this questionnaire",
                  border: "border-green-100 dark:border-green-800/40",
                  bg: "from-green-50/80 to-white dark:from-green-900/15 dark:to-gray-900",
                  iconBg: "bg-green-100 dark:bg-green-900/40",
                  body: (
                    <>
                      <p className="mb-2">
                        This questionnaire is designed to help you and Sandvik
                        determine whether any products or services fall within
                        the scope of the Data Act, identify the role(s) of
                        Sandvik, and define relevant data categories and scope.
                      </p>
                      <p>
                        This questionnaire applies to each individual product
                        (physical items/hardware) and digital service (software)
                        intended for sale and/or manufactured for external
                        customers by Sandvik. It serves as an initial assessment
                        which can support a better understanding of compliance
                        requirements and help mitigate any regulatory risks
                        associated with the Data Act.
                      </p>
                    </>
                  ),
                },
                {
                  key: "howto",
                  emoji: "💡",
                  title: "How to use & guidance",
                  border: "border-amber-200 dark:border-amber-700/40",
                  bg: "from-amber-50/80 to-white dark:from-amber-900/15 dark:to-gray-900",
                  iconBg: "bg-amber-100 dark:bg-amber-900/40",
                  body: (
                    <>
                      <p>
                        This questionnaire is here to help guide you through the
                        process and serves as a practical self-help resource.
                        Besides the questions guiding you through this
                        assessment, each question also has further information
                        marked as &ldquo;Guidance&rdquo;. This Guidance includes
                        further explanation, examples and decision trees for the
                        relevant question and/or section of the question. For
                        further guidance, please visit the{" "}
                        <a
                          href="https://digital-strategy.ec.europa.eu/en/library/commission-publishes-frequently-asked-questions-about-data-act"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-700 dark:text-amber-300 font-medium underline hover:text-amber-800 dark:hover:text-amber-200"
                        >
                          EU Commission FAQs
                        </a>{" "}
                        and/or the Sandvik Guideline for Compliance with the EU
                        Data Act (
                        <a
                          href="https://sandvik.sharepoint.com/:w:/r/teams/digitalcompliance/Shared%20Documents/Data%20Act/Data%20Act%20Guideline%20-%20V8.docx?d=w0d22a02332f94d5b8b35702f0e27924f&csf=1&web=1&e=kZAass"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-700 dark:text-amber-300 font-medium underline hover:text-amber-800 dark:hover:text-amber-200"
                        >
                          Data Act Guideline - V8.docx
                        </a>
                        ). Please keep in mind that this questionnaire is for
                        guidance purposes only and is not a replacement for a
                        comprehensive legal review.
                      </p>
                    </>
                  ),
                },
              ];
              return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
                  {cards.map((c) => (
                    <div
                      key={c.key}
                      className={`rounded-lg border ${c.border} bg-gradient-to-b ${c.bg} p-5 flex flex-col`}
                    >
                      <div className="flex items-center gap-2.5 mb-3">
                        <div
                          className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 shadow-sm ${c.iconBg}`}
                        >
                          <span className="text-base">{c.emoji}</span>
                        </div>
                        <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">
                          {c.title}
                        </h4>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                        {c.body}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Sticky footer actions */}
          <div className="flex items-center justify-center gap-3 flex-shrink-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 rounded-b-lg">
            <button
              onClick={() => navigate(`/products/${productId}`)}
              className="btn btn-outline text-sm px-5 py-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to Product
            </button>
            <button
              onClick={() => setAssessmentStarted(true)}
              className="btn btn-primary text-sm px-5 py-2 shadow-md shadow-blue-200 dark:shadow-blue-900/30"
            >
              <PlayCircle className="h-4 w-4 mr-1.5" />
              Begin Assessment
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Section navigation tabs at the top */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex flex-col min-h-0 relative">
            <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-center flex-wrap gap-1">
                {visibleSections.map((section, idx) => {
                  const sectionQuestions = getVisibleQuestions(
                    section,
                    answersList,
                  ).filter((q) => !q.infoOnly);
                  const answered = sectionQuestions.filter(
                    (q) => !!answers[q.id]?.answer,
                  ).length;
                  const total = sectionQuestions.length;
                  const isComplete = total > 0 && answered === total;
                  const isActive = idx === activeSectionIdx;
                  const displayTitle = sectionDisplayTitle(section, pathKey);
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSectionIdx(idx)}
                      title={displayTitle}
                      className={`inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] font-semibold transition-all ${
                        isActive
                          ? "bg-primary-600 text-white ring-1 ring-primary-400"
                          : isComplete
                            ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      {isComplete && <span aria-hidden="true">✓</span>}
                      <span>{displayTitle}</span>
                      {total > 0 && (
                        <span
                          className={`tabular-nums text-[10px] font-medium ${
                            isActive
                              ? "text-primary-100"
                              : isComplete
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                          }`}
                        >
                          {answered}/{total}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active section questions */}
            <div className="px-4 py-3 flex-1 overflow-y-auto min-h-0">
              {currentSection && (
                <div className="max-w-3xl mx-auto">
                  {/* Section header */}
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {sectionDisplayTitle(currentSection, pathKey)}
                    </h2>
                    {currentSection.description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {currentSection.description}
                      </p>
                    )}
                  </div>

                  {/* Questions */}
                  <div className="space-y-8">
                    {visibleQuestions.map((question) => {
                      // Skip child questions that are driven by the linked
                      // product picker when the trigger (Q2.5 / Q2.11) is YES.
                      // Their answers are still saved via the composite flow,
                      // so the result computation remains unchanged.
                      if (
                        answers["Q2.5"]?.answer === "yes" &&
                        ["Q2.6", "Q2.7", "Q2.8"].includes(question.id)
                      ) {
                        return null;
                      }
                      if (
                        answers["Q2.11"]?.answer === "yes" &&
                        ["Q2.12", "Q2.14", "Q2.15"].includes(question.id)
                      ) {
                        return null;
                      }

                      const a = answers[question.id];
                      const prevAnswer = previousAnswers.find(
                        (pa) => pa.questionId === question.id,
                      );
                      // Override Q1.5 options with API-fetched divisions
                      const enrichedQuestion =
                        question.id === "Q1.5" && divisionOptions
                          ? { ...question, options: divisionOptions }
                          : question;

                      if (enrichedQuestion.infoOnly) {
                        return (
                          <div
                            key={question.id}
                            ref={(el) => {
                              questionRefs.current[question.id] = el;
                            }}
                            className="pb-2 last:pb-0 pl-4 border-l-4 border-gray-200 dark:border-gray-700"
                          >
                            <DataActAssessmentQuestion
                              question={enrichedQuestion}
                              answer={a?.answer ?? ""}
                              onChange={handleChange}
                              disabled
                              pathKey={pathKey}
                            />
                          </div>
                        );
                      }

                      const isLinkedTrigger =
                        (question.id === "Q2.5" &&
                          answers["Q2.5"]?.answer === "yes") ||
                        (question.id === "Q2.11" &&
                          answers["Q2.11"]?.answer === "yes");
                      const linkedMode:
                        | "related-service"
                        | "connected-product" =
                        question.id === "Q2.5"
                          ? "related-service"
                          : "connected-product";

                      const isAnswered = !!a?.answer;
                      const railClass = isAnswered
                        ? "border-l-4 border-green-400 dark:border-green-600"
                        : question.required
                          ? "border-l-4 border-amber-400 dark:border-amber-500"
                          : "border-l-4 border-gray-200 dark:border-gray-700";
                      const isNew = !seenQuestionIds.current.has(question.id);
                      seenQuestionIds.current.add(question.id);

                      return (
                        <div
                          key={question.id}
                          ref={(el) => {
                            questionRefs.current[question.id] = el;
                          }}
                          data-question-id={question.id}
                          className={`pl-4 pb-8 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${railClass} ${
                            isNew ? "da-question-enter" : ""
                          }`}
                        >
                          <DataActAssessmentQuestion
                            question={enrichedQuestion}
                            answer={a?.answer ?? ""}
                            onChange={handleChange}
                            disabled={!canEdit}
                            previousAnswer={prevAnswer}
                            previousVersion={previousVersion}
                            pathKey={pathKey}
                          />
                          {isLinkedTrigger && productId && (
                            <div className="mt-4">
                              <DataActLinkedProductFlow
                                productId={productId}
                                mode={linkedMode}
                                answers={answers}
                                onChange={handleChange}
                                onResetTriggerToNo={() => {
                                  // Clear picker state and the child answers
                                  // so nothing stale survives the flip.
                                  const ids = linkedFlowQuestionIds(linkedMode);
                                  const stateId =
                                    linkedFlowStateQuestionId(linkedMode);
                                  handleChange(question.id, "answer", "no");
                                  handleChange(stateId, "answer", "");
                                  handleChange(stateId, "details", "");
                                  handleChange(ids.name, "answer", "");
                                  handleChange(ids.owner, "answer", "");
                                  handleChange(ids.completed, "answer", "");
                                }}
                                onSaveBeforeNavigate={() =>
                                  saveMutation.mutateAsync({
                                    answers: answersToList(answers),
                                    version: String(currentVersion),
                                    currentPage: activeSectionIdx,
                                  })
                                }
                                disabled={!canEdit}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {visibleQuestions.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-gray-500 italic py-8 text-center">
                        No questions visible in this section based on your
                        current answers.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Floating "Next required question" pill */}
            {canEdit && (
              <div className="absolute right-4 bottom-16 z-10 pointer-events-none">
                <button
                  type="button"
                  onClick={goToNextRequired}
                  disabled={!nextRequired}
                  className={`pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition-all ${
                    nextRequired
                      ? "bg-primary-600 text-white hover:bg-primary-700"
                      : "bg-green-600 text-white cursor-default"
                  }`}
                >
                  {nextRequired ? (
                    <>
                      <ArrowDown className="h-4 w-4" />
                      Next required
                      <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/20 text-[11px] tabular-nums">
                        {remainingRequiredCount}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCheck className="h-4 w-4" />
                      All required answered
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Previous / Next navigation */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                onClick={() => setActiveSectionIdx((i) => Math.max(0, i - 1))}
                disabled={activeSectionIdx === 0}
                className="btn btn-outline text-sm disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {activeSectionIdx + 1} / {visibleSections.length}
              </span>
              <button
                onClick={() =>
                  setActiveSectionIdx((i) =>
                    Math.min(visibleSections.length - 1, i + 1),
                  )
                }
                disabled={activeSectionIdx === visibleSections.length - 1}
                className="btn btn-outline text-sm disabled:opacity-40"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0 mt-2">
            {canEdit ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving || isSubmitting}
                  className="btn btn-outline"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Draft"}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSaving || isSubmitting}
                  className="btn btn-primary"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isSubmitting ? "Submitting..." : "Submit Assessment"}
                </button>
              </>
            ) : (
              <>
                <div />
                <button
                  onClick={() => navigate(`/products/${productId}`)}
                  className="btn btn-outline"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Product
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
